import { useMemo } from 'react';

export interface TrendPoint {
  label: string;
  value: number;
  /** Pre-formatted value, when the caller has one (else `format(value)`). */
  fmt?: string;
}

// Round a maximum up to a tidy gridline value (1 / 2 / 2.5 / 5 × 10^n).
function niceCeil(n: number) {
  if (n <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(n)));
  const r = n / p;
  // Finer steps so a $510 month tops out at $600, not $1,000 (bars stay tall).
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => r <= s) ?? 10;
  return step * p;
}

/**
 * Twelve months of one number, drawn so the shape is readable at a glance:
 * gridlines with values, a label on every bar, the current month in full
 * colour, a dashed average line, and a strip of what changed. Months before
 * the first dollar read as a baseline tick, not an empty hole.
 */
export function TrendChart({
  points,
  format,
  unitLabel = '',
  fill = 'bg-brand',
  fillSoft = 'bg-brand/45',
  height = 'h-48',
}: {
  points: TrendPoint[];
  format: (v: number) => string;
  /** e.g. "/mo" after the average figure. */
  unitLabel?: string;
  fill?: string;
  fillSoft?: string;
  height?: string;
}) {
  const stats = useMemo(() => {
    const vals = points.map((p) => p.value);
    const max = Math.max(0, ...vals);
    const firstIdx = vals.findIndex((v) => v > 0);
    const active = firstIdx >= 0 ? vals.slice(firstIdx) : [];
    const avg = active.length ? active.reduce((s, v) => s + v, 0) / active.length : 0;
    const last = vals[vals.length - 1] ?? 0;
    const prev = vals[vals.length - 2] ?? 0;
    const delta = prev > 0 ? (last - prev) / prev : null;
    let bestIdx = -1;
    vals.forEach((v, i) => { if (v > 0 && (bestIdx < 0 || v > vals[bestIdx])) bestIdx = i; });
    return { max, top: niceCeil(max), avg, last, prev, delta, bestIdx, firstIdx, any: max > 0 };
  }, [points]);

  const compact = (v: number) => {
    const s = format(v);
    // "$1,250.00" → "$1,250" on the axis; hours and counts pass through.
    return s.replace(/\.00$/, '');
  };
  const lastIdx = points.length - 1;
  const gridlines = [1, 0.75, 0.5, 0.25];

  if (!stats.any) {
    return (
      <div className={`${height} flex items-center justify-center rounded-lg bg-slate-50 text-sm text-ink-muted`}>
        Nothing in the last 12 months yet — the first charge draws the first bar.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-muted">
        <span>
          This month <strong className="font-semibold text-navy-900">{format(stats.last)}</strong>
        </span>
        {stats.delta != null ? (
          <span>
            vs last month{' '}
            <strong className={`font-semibold ${stats.delta >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {stats.delta >= 0 ? '▲' : '▼'} {Math.abs(Math.round(stats.delta * 100))}%
            </strong>
          </span>
        ) : stats.prev === 0 && stats.last > 0 ? (
          <span>vs last month <strong className="font-semibold text-emerald-700">new</strong></span>
        ) : null}
        {stats.bestIdx >= 0 && (
          <span>
            Best <strong className="font-semibold text-navy-900">{points[stats.bestIdx].label} · {format(points[stats.bestIdx].value)}</strong>
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="inline-block w-4 border-t border-dashed border-amber-500" />
          Average <strong className="font-semibold text-navy-900">{format(stats.avg)}{unitLabel}</strong>
        </span>
      </div>

      <div className="flex gap-2">
        {/* Axis */}
        <div className={`relative ${height} w-12 shrink-0 text-[10px] tabular-nums text-ink-muted`}>
          {gridlines.map((g) => (
            <span key={g} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - g) * 100}%` }}>
              {compact(stats.top * g)}
            </span>
          ))}
          <span className="absolute bottom-0 right-0 translate-y-1/2">0</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className={`relative ${height}`}>
            {/* Gridlines */}
            {gridlines.map((g) => (
              <div key={g} className="absolute inset-x-0 border-t border-slate-200/80" style={{ top: `${(1 - g) * 100}%` }} />
            ))}
            <div className="absolute inset-x-0 bottom-0 border-t border-slate-300" />
            {/* Average line */}
            {stats.avg > 0 && (
              <div
                className="absolute inset-x-0 border-t border-dashed border-amber-500/70"
                style={{ top: `${(1 - stats.avg / stats.top) * 100}%` }}
              />
            )}
            {/* Bars */}
            <div className="absolute inset-0 flex items-end gap-1 sm:gap-2">
              {points.map((p, i) => {
                const pct = (p.value / stats.top) * 100;
                const current = i === lastIdx;
                const label = p.fmt ?? compact(p.value);
                return (
                  <div key={i} className="group relative flex h-full flex-1 flex-col items-center justify-end" title={`${p.label}: ${p.fmt ?? format(p.value)}`}>
                    {p.value > 0 && (
                      <span
                        className={`mb-1 hidden whitespace-nowrap text-[10px] font-semibold tabular-nums sm:block ${current ? 'text-navy-900' : 'text-ink-muted'}`}
                      >
                        {label}
                      </span>
                    )}
                    <div
                      className={`w-full rounded-t transition ${p.value > 0 ? (current ? fill : fillSoft) : 'bg-slate-200'}`}
                      style={{ height: p.value > 0 ? `${Math.max(pct, 1.5)}%` : 2 }}
                    />
                    {/* Phone: value on hover/tap only — twelve labels won't fit */}
                    {p.value > 0 && (
                      <span className="pointer-events-none absolute -top-6 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-navy-900 px-1.5 py-0.5 text-[10px] text-white group-hover:block sm:group-hover:hidden">
                        {label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-1.5 flex gap-1 sm:gap-2">
            {points.map((p, i) => (
              <div key={i} className={`flex-1 text-center text-[10px] ${i === lastIdx ? 'font-semibold text-navy-900' : 'text-ink-muted'}`}>
                {p.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
